import { Injectable, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, Subject, firstValueFrom, from, of } from "rxjs";
import { JobScheduleDTO } from "../models/JobScheduleDTO";
import { environment } from "../../../core/config/environment";
import { InspectionJobDTO } from "../models/InspectionJobDTO";
import { catchError, finalize, shareReplay, switchMap, tap } from "rxjs/operators";
import { db, InspectionJob, JobIssueVendor, Media, SyncableStatus } from "../../../core/database/db";
import { ZoneDTO } from "../../zones/models/ZoneDTO";
import { AreaDTO } from "../../areas/models/AreaDTO";
import { ComponentDTO } from "../../components/models/ComponentDTO";
import { AssertionDTO } from "../../assertions/models/AssertionDTO";
import { AssertionIssueDTO, JobIssueDTO } from "../../jobIssues/models/IssueDTO";
import { JobAssetDto } from "../../media/models/JobAssetDto";
import { NetworkStatusService } from "../../../shared/services/network-status.service";
import { JobWithLot } from "../models/JobWithLot";
import JobDataService from "./job-data-service";
import { LoggerService } from "../../../shared/services/logger.service";
import { LotDto } from "../../lots/models/LotDto";
import { DateUtilsService } from "../../../shared/services/date.service";
import { UpdateSpec } from "dexie";
import { JobIssueVendorDTO } from "../../vendors/models/JobIssueVendorDTO";

@Injectable({ providedIn: "root" })
export class CustomerCareManagerScheduleService {

  private readonly url =
    `${environment.backend.apiBaseURL}/api/JobSchedule/GetCustomerCareManagerJobSchedule`;
  private isFetchingSchedule = new BehaviorSubject<boolean>(true);
  public isFetchingSchedule$ = this.isFetchingSchedule.asObservable();
  /** Signal mirror of `isFetchingSchedule$`, safe to bind directly to a loading indicator on any page. */
  readonly isFetchingScheduleSignal = signal(false);
  private inFlightSchedule$: Observable<InspectionJob[]> | null = null;

  /** Emits once a background schedule sync has successfully written fresh data to IndexedDB, so
   *  other already-open pages (e.g. bq7-root) know they should re-read their own local data. */
  public readonly scheduleRefreshed = new Subject<void>();

  isOnline: boolean = true;

  constructor(
    private http: HttpClient,
    private jobDataService: JobDataService,
    private logger: LoggerService,
    private networkService: NetworkStatusService,
    private dateUtils: DateUtilsService
  ) {

    this.networkService.online$.subscribe(val => {
      this.isOnline = val;
    });
  }

  private async syncIssueData(issueData: { issue: AssertionIssueDTO }[]) {
    const issueDataFromApi = issueData.filter(i => !!i && !!i.issue.identifier);

    await db.transaction("rw", [db.assertionIssue, db.media, db.syncStatus], async () => {
      db.setSuppressHooks(true);

      const localIssueData = await db.assertionIssue
        .where("identifier")
        .anyOf(issueDataFromApi.map(i => i.issue.identifier))
        .toArray();

      const newIssues = issueDataFromApi
        .filter(i => !localIssueData.find(localIss => localIss.identifier === i.issue.identifier));
      const updateIssues: (AssertionIssueDTO & { id: number | undefined })[] = localIssueData.map(localIss => {
        const apiIssue = issueDataFromApi.find(apiIss => apiIss.issue.identifier === localIss.identifier);

        if (apiIssue
          && localIss.syncStatus !== SyncableStatus.Pending
          && new Date(localIss.updatedAt).getTime() < new Date(apiIssue.issue.updatedAt).getTime()
        ) {
          return {
            ...apiIssue.issue,
            id: localIss.id,
            assertionIdentifier: apiIssue.issue.assertionIdentifier,
            isSelfAssigned: apiIssue.issue.isSelfAssigned ?? localIss.isSelfAssigned,
            // Accepted BQ6 carryover clones link back via parentIdentifier; the API may omit or
            // null this field on pull sync, which would unlock notes/photos after an app restart.
            parentIdentifier: apiIssue.issue.parentIdentifier ?? localIss.parentIdentifier,
          };

        }

        return null;
      }).filter(issue => !!issue);

      const apiIssueIds = new Set(issueDataFromApi.map(i => i.issue.identifier));

      const issuesToCleanup = localIssueData.filter(localIssue =>
        localIssue.syncStatus === SyncableStatus.Completed &&
        !apiIssueIds.has(localIssue.identifier)
      );

      // Add new issues
      if (newIssues.length > 0) {
        await db.batchBulkOperation("assertionIssue:add", newIssues, async newIssues => {
          await db.assertionIssue.bulkAdd(newIssues.map(i => ({
            ...i.issue,
            assertionIdentifier: i.issue.assertionIdentifier,
            isSelfAssigned: i.issue.isSelfAssigned ?? false,
            syncStatus: SyncableStatus.Completed,
            deleted: i.issue.deleted === true ? 1 : 0,
          })));
        }, this.logger);
      }

      // Update existing issues
      if (updateIssues.length > 0) {
        await db.batchBulkOperation("assertionIssue:update", updateIssues, async updateIssues => {
          await db.assertionIssue.bulkUpdate(updateIssues.map(localIssue => ({
            key: localIssue.id!,
            changes: {
              ...localIssue,
              deleted: localIssue.deleted === true ? 1 : 0,
            }
          })));
        }, this.logger);
      }

      // Remove stale issues
      if (issuesToCleanup.length > 0) {
        await db.batchBulkOperation("assertionIssue:delete", issuesToCleanup, async issuesToCleanup => {
          await db.assertionIssue.bulkDelete(issuesToCleanup.map(i => i.id!));
        }, this.logger);
      }
    });

    await this.syncJobIssueVendorData(issueDataFromApi.map(x => x.issue));
  }

  private async syncJobIssueVendorData(issueData: JobIssueDTO[]): Promise<void> {
    const issueIdentifiers = issueData
      .filter(issue => !!issue && !!issue.identifier)
      .map(issue => issue.identifier);

    if (issueIdentifiers.length === 0) {
      return;
    }

    // Extract all vendor assignments from the issue data
    const vendorAssignments: (JobIssueVendorDTO)[] = issueData
      .filter(issue => issue.jobIssueVendors && issue.jobIssueVendors.length > 0)
      .flatMap(issue => issue.jobIssueVendors!.map(vendor => ({
        ...vendor,
        issueIdentifier: issue.identifier
      })));

    await db.transaction("rw", [db.jobIssueVendor, db.media, db.syncStatus], async () => {
      db.setSuppressHooks(true);

      try {
        // Get existing vendor assignments for all synced issues, including issues that now have zero vendors
        const existingVendors = await db.jobIssueVendor
          .where("issueIdentifier")
          .anyOf(issueIdentifiers)
          .toArray();

        // Find new vendor assignments to add
        const newVendors = vendorAssignments.filter(apiVendor =>
          !existingVendors.find(localVendor =>
            localVendor.issueVendorIdentifier === apiVendor.issueVendorIdentifier
          )
        );

        // Find vendor assignments to update
        const updateVendors: (JobIssueVendorDTO & { id: number; issueIdentifier: string })[] = existingVendors
          .map(localVendor => {
            const apiVendor = vendorAssignments.find(api =>
              api.issueVendorIdentifier === localVendor.issueVendorIdentifier
            );

            if (apiVendor
              && localVendor.syncStatus !== SyncableStatus.Pending
              && new Date(localVendor.updatedAt).getTime() < new Date(apiVendor.updatedAt).getTime()) {
              return {
                ...apiVendor,
                id: localVendor.id!,
              };
            }

            return null;
          }).filter(vendor => !!vendor);

        // Build set of API vendor identifiers per issue for cleanup, including empty sets for issues with no assignments
        const apiVendorIdsByIssue = new Map<string, Set<string>>();
        issueIdentifiers.forEach(issueIdentifier => {
          apiVendorIdsByIssue.set(issueIdentifier, new Set<string>());
        });

        vendorAssignments.forEach(vendor => {
          apiVendorIdsByIssue.get(vendor.issueIdentifier)!.add(vendor.issueVendorIdentifier);
        });

        const vendorsToCleanup = existingVendors.filter(localVendor => {
          const apiVendorIds = apiVendorIdsByIssue.get(localVendor.issueIdentifier);

          return localVendor.syncStatus === SyncableStatus.Completed &&
            !!apiVendorIds &&
            apiVendorIds.has(localVendor.issueVendorIdentifier) === false;
        });

        // Add new vendor assignments
        if (newVendors.length > 0) {
          await db.batchBulkOperation("jobIssueVendor:add", newVendors, async vendors => {
            await db.jobIssueVendor.bulkAdd(vendors.map<JobIssueVendor>(vendor => ({
              issueVendorIdentifier: vendor.issueVendorIdentifier,
              externalVendorId: vendor.externalVendorId,
              issueIdentifier: vendor.issueIdentifier,
              updatedAt: new Date(vendor.updatedAt),
              createdAt: new Date(vendor.createdAt),
              isSent: vendor.isSent || !!vendor.lastSentAt,
              viewedAt: vendor.viewedAt ? new Date(vendor.viewedAt) : undefined,
              lastSentAt: vendor.lastSentAt ? new Date(vendor.lastSentAt) : undefined,
              vendorName: vendor.vendorName,
              syncStatus: SyncableStatus.Completed
            })));
          }, this.logger);
        }

        // Update existing vendor assignments
        if (updateVendors.length > 0) {
          await db.batchBulkOperation("jobIssueVendor:update", updateVendors, async vendors => {
            await db.jobIssueVendor.bulkUpdate(vendors.map<{
              key: number;
              changes: UpdateSpec<JobIssueVendor>;
            }>(apiVendor => ({
              key: apiVendor.id!,
              changes: {
                externalVendorId: apiVendor.externalVendorId,
                updatedAt: new Date(apiVendor.updatedAt),
                createdAt: new Date(apiVendor.createdAt),
                isSent: apiVendor.isSent,
                viewedAt: apiVendor.viewedAt ? new Date(apiVendor.viewedAt) : undefined,
                lastSentAt: apiVendor.lastSentAt ? new Date(apiVendor.lastSentAt) : undefined,
                vendorName: apiVendor.vendorName,
                syncStatus: SyncableStatus.Completed
              }
            })));
          }, this.logger);
        }

        // Remove stale vendor assignments
        if (vendorsToCleanup.length > 0) {
          await db.batchBulkOperation("jobIssueVendor:delete", vendorsToCleanup, async vendors => {
            await db.jobIssueVendor.bulkDelete(vendors.map(v => v.id!));
          }, this.logger);
        }
      } finally {
        db.setSuppressHooks(false);
      }
    });
  }

  private async syncMediaData(mediaData: Media[], knownParentIdentifiers: string[] = []) {
    const mediaDataFromApi = mediaData.filter(m => !!m && !!m.fileIdentifier);

    // A parent (e.g. an assertion or issue) whose last photo was removed server-side has no entries in
    // mediaDataFromApi at all, so it must still be included here explicitly - otherwise the stale local
    // copy on another device is never even looked up below, let alone cleaned up.
    const parentIdentifiersToScan = Array.from(new Set([
      ...mediaDataFromApi.map(m => m.parentIdentifier),
      ...knownParentIdentifiers
    ]));

    await db.transaction("rw", [db.media, db.syncStatus], async () => {
      db.setSuppressHooks(true);

      const localMediaData = parentIdentifiersToScan.length > 0
        ? await db.media
          .where("parentIdentifier")
          .anyOf(parentIdentifiersToScan)
          .toArray()
        : [];

      const newMediaData = mediaDataFromApi.filter(apiMedia =>
        !localMediaData.find(local =>
          local.fileIdentifier === apiMedia.fileIdentifier
        )
      );

      const updateMediaData: (Media & { id: number })[] = localMediaData.map(localMedia => {
        const apiMedia = mediaDataFromApi.find(media => media.fileIdentifier === localMedia.fileIdentifier
        );

        if (apiMedia
          && localMedia.syncStatus !== SyncableStatus.Pending
          && new Date(localMedia.updatedAt).getTime() < new Date(apiMedia.updatedAt).getTime()
        ) {
          return {
            ...apiMedia,
            id: localMedia.id!,
          };
        }

        return null;
      }).filter(i => !!i);

      const apiFileIds = new Set(mediaDataFromApi.map(m => m.fileIdentifier));
      const parentIdentifiersInResponse = new Set(parentIdentifiersToScan);

      const mediaToCleanup = localMediaData.filter(localMedia =>
        localMedia.syncStatus === SyncableStatus.Completed &&
        parentIdentifiersInResponse.has(localMedia.parentIdentifier) &&
        !apiFileIds.has(localMedia.fileIdentifier)
      );

      const mediaToInsert: Media[] = newMediaData.filter(m => m.fileIdentifier && m.fileIdentifier.trim() !== "");

      // Add new media
      if (mediaToInsert.length > 0) {
        await db.batchBulkOperation("media:add", mediaToInsert, async mediaToInsert => {
          await db.media.bulkAdd(mediaToInsert);
        }, this.logger);
      }

      // Update existing media
      if (updateMediaData.length > 0) {
        await db.batchBulkOperation("media:update", updateMediaData, async updateMediaData => {
          await db.media.bulkUpdate(updateMediaData.map(updatedMedia => ({
            key: updatedMedia.id!,
            changes: {
              updatedAt: updatedMedia.updatedAt,
              imageType: updatedMedia.imageType,
              resourceUri: updatedMedia.resourceUri,
              syncStatus: SyncableStatus.Completed,
            }
          })));
        }, this.logger);
      }

      // Remove stale media
      if (mediaToCleanup.length > 0) {
        await db.batchBulkOperation("media:delete", mediaToCleanup, async mediaToCleanup => {
          await db.media.bulkDelete(mediaToCleanup.map(m => m.id!));
        }, this.logger);
      }
    });
  }

  private async syncJobStructureData(jobs: JobWithLot[]): Promise<void> {
    // Extract and deduplicate zone data - zones are shared across jobs with the same lot
    const allZones = new Map<number, { zone: ZoneDTO, lotId: number }>();
    jobs.forEach(job => {
      job.lot?.zones?.forEach((z: unknown) => {
        const zone = z as ZoneDTO;
        if (!allZones.has(zone.id)) {
          allZones.set(zone.id, { zone, lotId: job.lotId });
        }
      });
    });

    // Sync unique zones only
    const uniqueZoneData = Array.from(allZones.values()).map(({ zone, lotId }) => ({ ...zone, lotId }));
    await this.jobDataService.syncZoneData(uniqueZoneData);

    // Extract area data for each job using the deduplicated zones
    const areaData = jobs.flatMap(job =>
      job.lot?.zones?.flatMap((z: unknown) => {
        const zone = z as ZoneDTO;
        return zone.areas.flatMap(a => ({
          area: a,
          zoneId: zone.id
        }));
      }) || []
    );
    const _insertedAreaMetadata = await this.jobDataService.syncAreaData(areaData);

    // Extract component data
    const componentData = jobs.flatMap(job =>
      job.lot?.zones?.flatMap((z: unknown) => {
        const zone = z as ZoneDTO;
        return zone.areas.flatMap(a =>

          a.components.flatMap(c => ({
            component: c,
            areaIdentifier: a.identifier,
            lotId: job.lot?.id ?? 0
          }))
        );
      }) || []
    );
    const componentMetadata = await this.jobDataService.syncComponentData(componentData);

    // Extract assertion data
    const assertionData = jobs
      .flatMap(j => {
        const zones = j.lot?.zones || [];
        return zones.flatMap((z: unknown) => {
          const zone = z as ZoneDTO;
          const areas = zone.areas || [];
          return areas.flatMap((a: AreaDTO) => {
            const components = a.components || [];
            return components.flatMap((c: ComponentDTO) => {
              const assertions = c.assertions || [];
              return assertions.map((assert: AssertionDTO) => {
                let componentId = c.id;

                if (!!componentId === false) {
                  componentId = componentMetadata.insertedComponents[c.identifier] || componentMetadata.existingComponents[c.identifier];
                }

                if (!!componentId === false) {
                  this.logger.error(`[CustomerCareManagerScheduleService:syncJobStructureData] Missing componentId for assertion ${assert.identifier} and component ${c.identifier} and job ${j.id} during sync. This assertion will not be synced.`);
                }

                return {
                  assertion: assert,
                  componentId,
                  lotId: j.lot?.id ?? 0
                };
              });
            });
          });
        });
      });
    await this.jobDataService.syncAssertionData(assertionData);

    // Extract assertion issue data
    const issueData = assertionData
      .filter(a => a.assertion.issues)
      .flatMap(a => a.assertion.issues!.flatMap((i: AssertionIssueDTO) => ({
        issue: {
          ...i,
          severity: i.severity.toLowerCase(),
          assertionIdentifier: a.assertion.identifier
        }
      })));
    await this.syncIssueData(issueData);

    // Extract media data
    const normalizeMedia = (jobAsset: JobAssetDto, parentIdentifier: string): Media => ({
      uploaded: jobAsset.isUploaded ? 1 : 0,
      resourceUri: jobAsset.azureBlobUri,
      fileIdentifier: jobAsset.mediaGuid ?? "",
      updatedAt: new Date(jobAsset.updatedAt),
      parentIdentifier,
      deleted: 0,
      syncStatus: SyncableStatus.Completed,
      imageType: jobAsset.imageType
    });

    const mediaData = [
      ...assertionData.filter(a => a.assertion.media)
        .flatMap(a => a.assertion.media!.flatMap((m: JobAssetDto) => (
          normalizeMedia(m, a.assertion.identifier)
        ))),
      ...issueData.filter(a => a.issue.media)
        .flatMap(i => i.issue.media!.flatMap((m: JobAssetDto) => (
          normalizeMedia(m, i.issue.identifier)
        )))
    ];

    // Every assertion or issue currently being synced is a valid cleanup target, even ones with zero
    // media right now (e.g. one whose only photo was just removed on another device) - otherwise a
    // parent that drops to zero media never appears in mediaData at all, so its stale local copy is
    // never scanned.
    const knownMediaParentIdentifiers = [
      ...assertionData.map(a => a.assertion.identifier),
      ...issueData.map(a => a.issue.identifier)
    ];

    await this.syncMediaData(mediaData, knownMediaParentIdentifiers);
  }

  private hydrateJob(
    raw: JobWithLot & { isComplete?: boolean }
  ): JobWithLot {
    return {
      ...raw,
      scheduledDate: this.dateUtils.convertUtcDateToLocalDateOnly(raw.scheduledDate),
      isComplete: raw.isComplete ?? false
    };
  }

  public async syncServerData(dto: JobScheduleDTO, shouldPerformCleanup = true): Promise<JobScheduleDTO> {
    this.logger.info("[syncServerData] Syncing server data for Customer Care Manager schedule");

    const unifiedJobs = (dto as unknown as { jobs?: JobWithLot[] }).jobs ?? [];

    // Hydrate unified jobs list
    const allHydrated: JobWithLot[] = unifiedJobs.map(j => this.hydrateJob(j));

    // Sync lot data first
    await this.jobDataService.syncLotData(
      allHydrated.map(job => job.lot)
        .filter((lot): lot is LotDto => lot !== undefined)
    );

    const readyHydrated = allHydrated
      .slice()
      .sort((a, b) => a.scheduledDate.getTime() - b.scheduledDate.getTime());

    await db.transaction("readwrite", [
      "inspectionJob",
      "zone",
      "area",
      "component",
      "assertion",
      "assertionIssue",
      "jobIssueVendor",
      "media",
      "userVendorContact",
      "jobIssue",
      "jobSignature",
      "syncStatus"
    ], async () => {

      db.setSuppressHooks(true);


      await this.jobDataService.syncJobData(readyHydrated.map(j => {
        const { lot: _lot, ...jobWithoutLot } = j;
        return jobWithoutLot as InspectionJobDTO;
      }));

      // Runs even when cleanup is skipped: this corrects stale rows rather than deleting them, so a
      // background sync is a safe place to do it.
      await this.jobDataService.reconcileClosedJobs(dto.closedJobs ?? []);

      await this.syncJobStructureData(readyHydrated);

      if (shouldPerformCleanup) {
        await this.jobDataService.cleanupSupersededSameLotJobs(readyHydrated);
        await this.jobDataService.cleanupStaleJobs(readyHydrated);
      }
    });

    await this.jobDataService.customerCareManagerSyncCompleted();
    return {
      ...dto,
      jobs: readyHydrated.map(j => {
        const { lot: _lot, ...jobWithoutLot } = j;
        return jobWithoutLot as InspectionJobDTO;
      })
    };
  }

  /**
   * Reads whatever BQ7 InspectionJobs are already cached locally, with no network call.
   * Used to render the schedule immediately before/while a background refresh runs.
   */
  public getCustomerCareManagerScheduleFromCache(): Promise<InspectionJob[]> {
    return db.inspectionJob.where("buildQualityStep").equals(7)
      .toArray();
  }

  public getCustomerCareManagerSchedule(): Observable<InspectionJob[]> {
    if (this.isOnline === false) {
      return from(
        db.inspectionJob
          .where("buildQualityStep").equals(7)
          .toArray()
      );
    }

    if (this.inFlightSchedule$) {
      return this.inFlightSchedule$;
    }

    const request$ = from(this.http.get<JobScheduleDTO>(`${this.url}`).pipe(
      switchMap(dto => from(this.syncServerData(dto))),
      tap(() => this.scheduleRefreshed.next()),
      catchError(err => {
        this.logger.error("[getCustomerCareManagerSchedule] Error syncing server data", err);
        return of(null); // swallow error so pipeline continues
      }),
      switchMap(() => from(db.inspectionJob.where("buildQualityStep").equals(7)
        .toArray())),
      finalize(() => {
        this.isFetchingSchedule.next(false);
        this.isFetchingScheduleSignal.set(false);
        this.inFlightSchedule$ = null;
      }),
      shareReplay(1)
    ));

    this.isFetchingSchedule.next(true);
    this.isFetchingScheduleSignal.set(true);
    this.inFlightSchedule$ = request$;
    return request$;
  }

  public getCustomerCareManagerDataForSync(): Promise<JobScheduleDTO> {
    const url = `${environment.backend.apiBaseURL}/api/jobSchedule/GetCustomerCareManagerJobScheduleForDataSync`;
    return firstValueFrom(this.http.get<JobScheduleDTO>(url, {}));
  }
}